import {
  Alert,
  Drawer,
  Input,
  message,
  Upload,
  Table,
  Form,
  InputNumber,
  Slider,
} from "antd";
import { Controller } from "react-hook-form";
import React, { FC, useEffect, useReducer, useState } from "react";
import {
  DecimalInput,
  decimalFieldRules,
  formatCurrency,
  isValidDecimal,
  resourceLineTotal,
  toolLineTotal,
} from "./timelogAmountUtils";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "../../../components/Component";
import { Col, Row, Spinner } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { UploadOutlined } from "@ant-design/icons";
import { useLocation } from "react-router";
import { tasksApis } from "../../../api/NewTasks";
import { logHoursApis } from "../../../api/TimeLog";
import Swal from "sweetalert2";
import { issueApis } from "../../../api/Issue";
import TimeLogToolMaterialsEditor, {
  type TimeLogMaterialRow,
} from "./TimeLogToolMaterialsEditor";

interface UploadApiResponse {
  status: boolean;
  message: string;
  response?: Array<{ docId: number; filePath: string }>;
}

export interface TimeLogCreationData {
  date: Dayjs;
  user: number;
  linkedTask?: number | null;
  linkedIssue?: number | null;
  linkedEntity?: number | null;
  shiftId?: number | null;
  billingType?: string | null; // Changed to allow null explicitly
  taskCompletionPercentage?: number | null;
  startHour: string;
  startMinute: string;
  startMeridiem: "am" | "pm";
  endHour: string;
  endMinute: string;
  endMeridiem: "am" | "pm";
  notes?: string;
  attachedFiles?: File[];
  newfiledata?: Array<{
    indexOfFileLocated: number;
    linkType: string;
    linkTo: string;
    remarks: string;
    action: string;
    timeLogId?: number;
    docName: string;
  }>;
  deletedFileData?: Array<{
    docId: number;
  }>;
}

export interface ExtendedSelectOption extends SelectOption {
  type: "task" | "issue";
  taskOwnerName?: string;
  taskOwnerId?: number;
  issueAssignedToName?: string;
  issueAssignedToId?: number;
  activityId?: number;
  overallTaskCompletionPercentage?: number;
  disabled?: boolean;
}

interface ActivityResource {
  activityResourceId: number;
  taskResourceId?: number;
  laborTypeId: number;
  laborTypeName: string;
  laborCategoryId: number;
  laborCategory: string;
  status: string;
}

interface ResourceEditableCellProps extends React.HTMLAttributes<HTMLElement> {
  dataIndex: string;
  title: string;
  record: any;
  children: React.ReactNode;
  form: any;
  editing: boolean;
}

const ResourceEditableCell: React.FC<ResourceEditableCellProps> = ({
  dataIndex,
  title,
  record,
  children,
  form,
  editing,
  ...restProps
}) => {
  if (dataIndex === "resourceAtSite") {
    const fieldName = `${dataIndex}_${record?.activityResourceId}`;

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.resourceAtSite || undefined}
            rules={[
              {
                required: true,
                type: "number",
                min: 1,
                message: `${title} must be at least 1`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder="Enter count"
            />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  if (dataIndex === "actualQuantity" || dataIndex === "actualRate") {
    const fieldName = `${dataIndex}_${record?.activityResourceId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.[dataIndex]}
            rules={decimalFieldRules(title)}
          >
            <DecimalInput placeholder={`Enter ${title.toLowerCase()}`} />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

interface ToolEditableCellProps extends React.HTMLAttributes<HTMLElement> {
  dataIndex: string;
  title: string;
  record: any;
  children: React.ReactNode;
  form: any;
  editing: boolean;
}

const ToolEditableCell: React.FC<ToolEditableCellProps> = ({
  dataIndex,
  title,
  record,
  children,
  form,
  editing,
  ...restProps
}) => {
  if (dataIndex === "actualQuantity" || dataIndex === "actualRate") {
    const fieldName = `${dataIndex}_${record?.taskToolId}`;
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={fieldName}
            style={{ margin: 0 }}
            initialValue={record?.[dataIndex]}
            rules={decimalFieldRules(title)}
          >
            <DecimalInput placeholder={`Enter ${title.toLowerCase()}`} />
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  }
  return <td {...restProps}>{children}</td>;
};

const TimeSheetListCreation: FC<{
  projectId?: number;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  refreshData: () => void;
  linkedTask?: number | null;
  linkedIssue?: number | null;
  disableLinkField?: boolean;
  taskOwnerId?: number; // Add task owner ID for access control
  taskOwnerName?: string; // Add task owner name for display
  issueOwnerId?: number; // Add issue owner ID for access control
  issueOwnerName?: string; // Add issue owner name for display
  onTaskCompletionUpdate?: () => void; // Callback for task completion updates
}> = ({
  projectId: propProjectId,
  modalOpen,
  setModalOpen,
  refreshData,
  linkedIssue,
  linkedTask,
  disableLinkField = false,
  taskOwnerId,
  taskOwnerName,
  issueOwnerId,
  issueOwnerName,
  onTaskCompletionUpdate,
}) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  // Check if current user can add time logs
  const canAddTimeLog = () => {
    if (!currentUser) return false;

    // If no specific owner is provided (general time sheets page), allow all users to add time logs
    if (!taskOwnerId && !issueOwnerId) return true;

    // Check if user is admin - admin can always add time logs
    if (currentUser.genericRole?.toLowerCase() === "admin") return true;

    // For task-specific time log creation, only task owner can add
    if (taskOwnerId && currentUser.id === taskOwnerId) return true;

    // For issue-specific time log creation, only issue owner can add
    if (issueOwnerId && currentUser.id === issueOwnerId) return true;

    return false;
  };

  const [notes, setNotes] = useState("");
  const [taskDropdownData, setTaskDropdownData] = useState<SelectOption[]>([]);
  const [issueDropdownData, setIssueDropdownData] = useState<SelectOption[]>(
    []
  );
  const [combinedOptions, setCombinedOptions] = useState<
    ExtendedSelectOption[]
  >([]);

  const [disableSubmit, setDisableSubmit] = useState(false);
  const [shiftOptions, setShiftOptions] = useState<SelectOption[]>([]);
  const [selectedEntityOwnerName, setSelectedEntityOwnerName] = useState<
    string | null
  >(null);
  const [selectedEntityOwnerId, setSelectedEntityOwnerId] = useState<
    number | null
  >(null);
  const [attachedFiles, setAttachedFiles] = useState<any[]>([]);
  const [fileRemarks, setFileRemarks] = useState<{ [key: number]: string }>({});
  const [newFileData, setNewFileData] = useState<any[]>([]);
  const [deletedFileData, setDeletedFileData] = useState<any[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  // Resource allocation state
  const [activityResourcesList, setActivityResourcesList] = useState<
    ActivityResource[]
  >([]);
  const [selectedResourceKeys, setSelectedResourceKeys] = useState<React.Key[]>(
    []
  );
  const [editingResourceKeys, setEditingResourceKeys] = useState<string[]>([]);

  // Map of workUomId -> uomName, used to label planned quantities.
  const [workUomMap, setWorkUomMap] = useState<Record<number, string>>({});

  // Tools & Machinery state
  const [activityToolsList, setActivityToolsList] = useState<any[]>([]);
  const [selectedToolKeys, setSelectedToolKeys] = useState<React.Key[]>([]);
  const [editingToolKeys, setEditingToolKeys] = useState<string[]>([]);
  const [toolForm] = Form.useForm();

  // Per-tool actual material usage, keyed by taskToolId. Pre-populated from the
  // task tool's planned materials; only Actual Qty/Rate are captured.
  const [materialRowsByTool, setMaterialRowsByTool] = useState<
    Record<number, TimeLogMaterialRow[]>
  >({});
  const [materialsDrawerToolId, setMaterialsDrawerToolId] = useState<
    number | null
  >(null);
  // materialId -> name, to label the planned materials in the editor.
  const [materialNameMap, setMaterialNameMap] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    (async () => {
      try {
        const matRes = await tasksApis.fetchMachineryMaterialDropDown();
        if (matRes?.status) {
          const map: Record<number, string> = {};
          (matRes.data || []).forEach((m: any) => {
            map[m.id ?? m.materialId] = m.materialName ?? m.name ?? m.label;
          });
          setMaterialNameMap(map);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // Build the editable material rows for a tool: its planned task-tool materials
  // merged with any actuals already entered in this form.
  const getToolMaterialRows = (taskToolId: number): TimeLogMaterialRow[] => {
    if (materialRowsByTool[taskToolId]) return materialRowsByTool[taskToolId];
    const tool = activityToolsList.find((t) => t.taskToolId === taskToolId);
    return (tool?.toolMaterials || []).map(
      (m: any): TimeLogMaterialRow => ({
        taskToolMaterialId: m.taskToolMaterialId,
        materialId: m.materialId,
        subMaterialId: m.subMaterialId,
        plannedQuantity: m.quantity,
        plannedRate: m.rate,
        workUomName: m.workUomName,
      }),
    );
  };
  const [resourceForm] = Form.useForm();
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(
    null
  );
  const [minCompletionPercentage, setMinCompletionPercentage] =
    useState<number>(0);

  const location = useLocation();
  const { id: routeProjectId, projectName: routeProjectName } =
    location.state || {};
  // Prioritize propProjectId, then routeProjectId
  const currentProjectId = propProjectId || routeProjectId;

  const methods = useForm<TimeLogCreationData>({
    mode: "onChange",
    defaultValues: {
      date: dayjs(),
      user: currentUser?.userId || 1,
      linkedTask: linkedTask || null, // Set default value for linkedTask
      linkedIssue: linkedIssue || null, // Set default value for linkedIssue
      linkedEntity: linkedTask || linkedIssue || null,
      billingType: null, // Initialize billingType as null
      taskCompletionPercentage: null, // Initialize taskCompletionPercentage as null
      startHour: "09",
      startMinute: "00",
      startMeridiem: "am",
      endHour: "06",
      endMinute: "00",
      endMeridiem: "pm",
    },
  });

  const { handleSubmit, reset, watch } = methods;
  const selectedEntity = watch("linkedEntity");
  const watchedLinkedTask = watch("linkedTask");
  useEffect(() => {
    if (selectedEntity) {
      const entity = combinedOptions.find(
        (opt) => opt.value === selectedEntity
      );
      if (entity) {
        if (entity.type === "task") {
          methods.setValue("linkedTask", entity.value);
          methods.setValue("linkedIssue", null);
          setSelectedEntityOwnerName(entity.taskOwnerName || null);
          setSelectedEntityOwnerId(entity.taskOwnerId || null);

          const minCompletion = Math.min(
            100,
            Math.max(0, Number(entity.overallTaskCompletionPercentage ?? 0))
          );
          setMinCompletionPercentage(minCompletion);
          methods.setValue("taskCompletionPercentage", minCompletion as any, {
            shouldValidate: false,
            shouldDirty: false,
          });

          // Fetch task resources and tools directly using the task ID
          fetchWorkUoms();
          fetchTaskResources(entity.value);
          fetchTaskTools(entity.value);
        } else {
          methods.setValue("linkedIssue", entity.value);
          methods.setValue("linkedTask", null);
          setSelectedEntityOwnerName(entity.issueAssignedToName || null);
          setSelectedEntityOwnerId(entity.issueAssignedToId || null);
          setMinCompletionPercentage(0);

          // Clear resources and tools for issues
          setActivityResourcesList([]);
          setSelectedResourceKeys([]);
          setEditingResourceKeys([]);
          setActivityToolsList([]);
          setSelectedToolKeys([]);
          setEditingToolKeys([]);
          resourceForm.resetFields();
        }
      }
    }
  }, [selectedEntity, combinedOptions, methods]); // Added combinedOptions and methods to dependencies

  // Set initial owner info when modal opens with pre-selected task
  useEffect(() => {
    if (modalOpen && taskOwnerId && taskOwnerName && disableLinkField) {
      setSelectedEntityOwnerId(taskOwnerId);
      setSelectedEntityOwnerName(taskOwnerName);
    }
  }, [modalOpen, taskOwnerId, taskOwnerName, disableLinkField]);

  const fetchShiftDropdownData = async (projectId: number) => {
    try {
      const response = await logHoursApis.getShifts(projectId); // call your shift API
      const { data, status } = response;
      if (status) {
        const options = data?.map((shift: any) => ({
          label: shift.shiftName,
          value: shift.shiftId,
        }));
        setShiftOptions(options);
      } else {
        setShiftOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch shifts:", error);
      setShiftOptions([]);
    }
  };

  // Fetch the work-UOM master so planned quantities can show the unit name
  // even when the task resource/tool response returns only workUomId.
  const fetchWorkUoms = async () => {
    try {
      const response = await tasksApis.workUom({});
      const { data, status } = response || {};
      if (status && Array.isArray(data)) {
        const map: Record<number, string> = {};
        data.forEach((item: any) => {
          if (item?.workUomId != null) {
            map[Number(item.workUomId)] =
              item.uomName ?? item.workUomName ?? item.workUomShortCode;
          }
        });
        setWorkUomMap(map);
      }
    } catch (error) {
      console.error("Error fetching work UOMs:", error);
    }
  };

  // Resolve a record's UOM name from its own fields or the UOM master.
  const resolveUomName = (record: any): string =>
    record?.uomName ||
    record?.workUomName ||
    workUomMap[Number(record?.workUomId)] ||
    "";

  const fetchTaskResources = async (taskId: number) => {
    try {
      const response = await tasksApis.fetchTaskResources(taskId);
      const { data, status } = response || {};
      if (status && data) {
        // Map the response to match the expected format
        const formattedResources = data.map((resource: any) => ({
          activityResourceId: resource.activityResourceId,
          taskResourceId: resource.taskResourceId,
          laborTypeId: resource.laborTypeId,
          laborTypeName: resource.laborTypeName,
          laborCategoryId: resource.laborCategoryId,
          laborCategory: resource.laborCategoryName,
          resourceAtSite: undefined, // Empty for user to enter
          resourceCount: resource.resourceCount, // Display the actual resource count
          // Planned values (reference) from the task, when available
          plannedQuantity: resource.quantity,
          plannedRate: resource.rate,
          uomName: resource.workUomName ?? resource.uomName,
          workUomId: resource.workUomId,
          actualQuantity: undefined, // Empty for user to enter
          actualRate: undefined, // Empty for user to enter
          status: resource.status,
        }));
        setActivityResourcesList(formattedResources || []);
      } else {
        setActivityResourcesList([]);
      }
    } catch (error) {
      console.error("Error fetching task resources:", error);
      setActivityResourcesList([]);
    }
  };

  const fetchTaskTools = async (taskId: number) => {
    try {
      const response = await tasksApis.fetchTaskTools(taskId);
      const { data, status } = response || {};
      if (status && data) {
        // Map the response to match the expected format
        const formattedTools = data.map((tool: any) => ({
          taskToolId: tool.taskToolId,
          toolId: tool.toolId,
          toolName: tool.toolName,
          toolTypeName: tool.toolTypeName,
          count: tool.count,
          // Planned values (reference) from the task, when available
          serialNumber: tool.serialNumber,
          plannedQuantity: tool.quantity,
          plannedRate: tool.rate,
          uomName: tool.workUomName ?? tool.uomName,
          workUomId: tool.workUomId,
          actualQuantity: undefined, // Empty for user to enter
          actualRate: undefined, // Empty for user to enter
          // Planned split-materials (fixed list) the user logs actuals against.
          toolMaterials: tool.toolMaterials || [],
        }));
        setActivityToolsList(formattedTools || []);
      } else {
        setActivityToolsList([]);
      }
    } catch (error) {
      console.error("Error fetching task tools:", error);
      setActivityToolsList([]);
    }
  };

  const handleFileUpload = (info: any) => {
    if (info.file.status !== "uploading") {
      const fileObj = info.file.originFileObj;
      const fileIndex = attachedFiles.length;
      setAttachedFiles((prev) => [...prev, fileObj]);

      // Initialize empty remark for this file
      setFileRemarks((prev) => ({
        ...prev,
        [fileIndex]: "Related to timelog",
      }));

      // Create new file data entry
      const newFileEntry = {
        indexOfFileLocated: attachedFiles.length,
        linkType: "timelog",
        linkTo:
          combinedOptions
            .find((opt) => opt.value === selectedEntity)
            ?.label.replace(/^\[(Task|Issue)\]\s/, "") || "New Timelog",
        remarks: "Related to timelog",
        action: "Create",
        docName: fileObj.name,
      };
      setNewFileData((prev) => [...prev, newFileEntry]);
    }
  };

  const handleRemoveFile = (fileToRemove: any) => {
    const index = attachedFiles.indexOf(fileToRemove);
    setAttachedFiles((prev) => prev.filter((file) => file !== fileToRemove));
    setFileRemarks((prev) => {
      const newRemarks = { ...prev };
      delete newRemarks[index];
      return newRemarks;
    });

    if (index !== -1) {
      setNewFileData((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const onResourceSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows?: any
  ) => {
    setSelectedResourceKeys(newSelectedRowKeys);
    if (!newSelectedRowKeys) {
      setEditingResourceKeys([]);
    }
    const editableRowKeys: any = [];
    newSelectedRowKeys?.forEach((key) => {
      const selectedRow = selectedRows.find(
        (row: any) => `${row.activityResourceId}` === key
      );
      editableRowKeys.push(key);
      if (selectedRow?.resourceCount) {
        resourceForm.setFieldsValue({
          [`resourceCount_${key}`]: selectedRow?.resourceCount,
        });
      }
    });
    setEditingResourceKeys(editableRowKeys);
  };

  const isResourceEditing = (record: any) =>
    editingResourceKeys.includes(`${record.activityResourceId}`);

  const onToolSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows?: any
  ) => {
    setSelectedToolKeys(newSelectedRowKeys);
    if (!newSelectedRowKeys) {
      setEditingToolKeys([]);
    }
    const editableRowKeys: any = [];
    newSelectedRowKeys?.forEach((key) => {
      const selectedRow = selectedRows.find(
        (row: any) => `${row.taskToolId}` === key
      );
      editableRowKeys.push(key);
      // Don't pre-fill actualCount - let user enter their own value
    });
    setEditingToolKeys(editableRowKeys);
  };

  const isToolEditing = (record: any) =>
    editingToolKeys.includes(`${record.taskToolId}`);

  // Bumped whenever a resource/tool form value changes so the line/section
  // totals re-render live.
  const [, forceRecalc] = useReducer((x: number) => x + 1, 0);

  // Resources section total = sum of (count x qty x rate) for selected rows.
  const computeResourceSectionTotal = (): number => {
    const values = resourceForm.getFieldsValue();
    return selectedResourceKeys.reduce<number>((sum, key) => {
      return (
        sum +
        resourceLineTotal(
          values[`resourceAtSite_${key}`],
          values[`actualQuantity_${key}`],
          values[`actualRate_${key}`],
        )
      );
    }, 0);
  };

  // Tools section total = sum of (qty x rate) for selected rows (no count).
  const computeToolSectionTotal = (): number => {
    const values = toolForm.getFieldsValue();
    return selectedToolKeys.reduce<number>((sum, key) => {
      return (
        sum +
        toolLineTotal(
          values[`actualQuantity_${key}`],
          values[`actualRate_${key}`],
        )
      );
    }, 0);
  };

  const submitTimeLog = async (data: TimeLogCreationData) => {
    const selectedDate = data.date;
    const now = dayjs();

    if (selectedDate.isAfter(now, "day")) {
      message.error("Time logging is not allowed for future dates.");
      return;
    }

    // Format start time
    const formatTime = (hour: string, minute: string, meridiem: string) => {
      const h = parseInt(hour);
      const isPM = meridiem.toLowerCase() === "pm";
      const finalHour = isPM ? (h % 12) + 12 : h % 12;
      return `${String(finalHour).padStart(2, "0")}:${minute}:00`;
    };

    const loginTime = formatTime(
      data.startHour,
      data.startMinute,
      data.startMeridiem
    );
    const logoutTime = formatTime(
      data.endHour,
      data.endMinute,
      data.endMeridiem
    );

    if (!currentProjectId) {
      message.error("Please fill in all mandatory fields.");
      return;
    }

    // Determine userId based on user role and selection
    let userId = currentUser?.userId || currentUser?.id;

    // For admin users, use the selected task/issue owner ID if available
    if (currentUser?.genericRole?.toLowerCase() === "admin") {
      if (selectedEntityOwnerId) {
        userId = selectedEntityOwnerId;
      } else if (taskOwnerId && disableLinkField) {
        // If we're in a task-specific context and no entity is selected, use task owner
        userId = taskOwnerId;
      }
    }

    // Convert userId to number if it's a string with "user-" prefix
    if (typeof userId === "string" && userId.startsWith("user-")) {
      userId = parseInt(userId.replace("user-", ""), 10);
    }

    // Get resource values from resourceForm - collect only selected resources (checkbox).
    // Actual Quantity and Actual Rate are mandatory for every selected row.
    const resourceFormValues = resourceForm.getFieldsValue();
    const resourceValidationErrors: string[] = [];
    const selectedActivityResources = selectedResourceKeys
      .map((key: React.Key) => {
        const activityResourceId = Number(key);
        const resourceAtSite = resourceFormValues[`resourceAtSite_${key}`];
        const actualQuantity = resourceFormValues[`actualQuantity_${key}`];
        const actualRate = resourceFormValues[`actualRate_${key}`];
        const resourceData = activityResourcesList.find(
          (r) => r.activityResourceId === activityResourceId
        );
        const label =
          resourceData?.laborTypeName || `Resource #${activityResourceId}`;

        if (!resourceAtSite || Number(resourceAtSite) <= 0) {
          resourceValidationErrors.push(
            `${label}: Resource at Site is required`
          );
        }
        if (!isValidDecimal(actualQuantity)) {
          resourceValidationErrors.push(
            `${label}: valid Actual Quantity is required`
          );
        }
        if (!isValidDecimal(actualRate)) {
          resourceValidationErrors.push(
            `${label}: valid Actual Rate is required`
          );
        }

        return {
          activityResourceId,
          taskResourceId: resourceData?.taskResourceId,
          resourceCount: Number(resourceAtSite),
          actualQuantity: Number(actualQuantity),
          actualRate: Number(actualRate),
        };
      })
      .filter(
        (
          resource: any
        ): resource is {
          activityResourceId: number;
          taskResourceId: number;
          resourceCount: number;
          actualQuantity: number;
          actualRate: number;
        } => resource !== null
      );

    // Get tool values from toolForm - collect only selected tools (checkbox).
    // Tools capture Actual Quantity and Actual Rate (no count); both mandatory.
    const toolFormValues = toolForm.getFieldsValue();
    const toolValidationErrors: string[] = [];
    const selectedActivityTools = selectedToolKeys
      .map((key: React.Key) => {
        const taskToolId = Number(key);
        const actualQuantity = toolFormValues[`actualQuantity_${key}`];
        const actualRate = toolFormValues[`actualRate_${key}`];
        const toolData = activityToolsList.find(
          (t) => t.taskToolId === taskToolId
        );
        const label = toolData?.toolName || `Tool #${taskToolId}`;

        if (!isValidDecimal(actualQuantity)) {
          toolValidationErrors.push(
            `${label}: valid Actual Quantity is required`
          );
        }
        if (!isValidDecimal(actualRate)) {
          toolValidationErrors.push(`${label}: valid Actual Rate is required`);
        }

        return {
          taskToolId: toolData?.taskToolId,
          actualQuantity: Number(actualQuantity),
          actualRate: Number(actualRate),
        };
      })
      .filter(
        (
          tool: any
        ): tool is {
          taskToolId: number;
          actualQuantity: number;
          actualRate: number;
        } => tool !== null
      );

    // Block submission until every selected resource/tool line is valid.
    const allValidationErrors = [
      ...resourceValidationErrors,
      ...toolValidationErrors,
    ];
    if (allValidationErrors.length > 0) {
      // Surface inline errors on the offending fields as well.
      resourceForm.validateFields().catch(() => {});
      toolForm.validateFields().catch(() => {});
      message.error(allValidationErrors[0]);
      return;
    }

    // 1️⃣ Construct taskTimelog JSON
    const taskTimelog = {
      taskId: data.linkedTask || null,
      projectId: Number(currentProjectId),
      subTaskId: null,
      issueId: data.linkedIssue || null,
      shiftId: data.shiftId ?? null,
      logDate: selectedDate.format("YYYY-MM-DD"),
      loginTime,
      logoutTime,
      billingType: data.billingType?.toLowerCase() || null,
      taskCompletionPercentage:
        data.taskCompletionPercentage !== null &&
        data.taskCompletionPercentage !== undefined
          ? Math.max(
              0,
              Number(data.taskCompletionPercentage) - minCompletionPercentage
            )
          : null,
      notes: notes && notes.trim() ? notes.trim() : null,
      userId: userId, // Add userId field
      timeLogResources:
        selectedActivityResources.length > 0
          ? selectedActivityResources.map((resource: any) => ({
              taskResourceId: resource.taskResourceId,
              actualResourceCount: resource.resourceCount,
              actualQuantity: resource.actualQuantity,
              actualRate: resource.actualRate,
            }))
          : null, // Add resources
      timeLogTools:
        selectedActivityTools.length > 0
          ? selectedActivityTools.map((tool: any) => {
              const rows = materialRowsByTool[tool.taskToolId] || [];
              const matLines = rows
                .filter(
                  (r) =>
                    (r.actualQuantity != null && `${r.actualQuantity}` !== "") ||
                    (r.actualRate != null && `${r.actualRate}` !== ""),
                )
                .map((r) => ({
                  taskToolMaterialId: r.taskToolMaterialId,
                  ...(r.actualQuantity != null && `${r.actualQuantity}` !== ""
                    ? { actualQuantity: Number(r.actualQuantity) }
                    : {}),
                  ...(r.actualRate != null && `${r.actualRate}` !== ""
                    ? { actualRate: Number(r.actualRate) }
                    : {}),
                }));
              return {
                taskToolId: tool.taskToolId,
                actualQuantity: tool.actualQuantity,
                actualRate: tool.actualRate,
                ...(matLines.length ? { toolMaterials: matLines } : {}),
              };
            })
          : null, // Add tools
    };

    // 2️⃣ Construct filesData JSON
    const filesData = {
      newTimeLogFileData: newFileData.map((f, idx) => ({
        ...f,
        indexOfFileLocated: idx,
        action: "Created",
      })),
      deletedTimeLogFileData: deletedFileData,
    };

    // 3️⃣ Prepare FormData
    const formData = new FormData();
    formData.append("taskTimelog", JSON.stringify(taskTimelog));
    formData.append("filesData", JSON.stringify(filesData));

    if (attachedFiles.length > 0) {
      attachedFiles.forEach((file) => {
        formData.append("files", file);
      });
    }

    try {
      setDisableSubmit(true);

      // 4️⃣ Call API with multipart/form-data
      const response = await logHoursApis.createNewTimeLog(formData);

      const { status, message: apiMessage } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Time Log Created",
          text: apiMessage || "Time log saved successfully.",
          confirmButtonText: "OK",
        });

        reset();
        setNotes("");
        setModalOpen(false);
        setActivityResourcesList([]);
        setSelectedResourceKeys([]);
        setEditingResourceKeys([]);
        resourceForm.resetFields();
        setSelectedActivityId(null);
        // Reset tools and machinery state
        setActivityToolsList([]);
        setSelectedToolKeys([]);
        setEditingToolKeys([]);
        toolForm.resetFields();
        setMinCompletionPercentage(0);
        // refreshData();
        setTimeout(() => refreshData(), 100);

        // Call task completion update callback if completion percentage was updated
        if (
          onTaskCompletionUpdate &&
          typeof onTaskCompletionUpdate === "function"
        ) {
          onTaskCompletionUpdate();
        }
      } else {
        message.error(apiMessage || "Failed to create time log");
      }
    } catch (err) {
      console.error("Time Log Submission Error:", err);
      message.error("Error while submitting time log");
    } finally {
      setDisableSubmit(false);
    }
  };

  // API call for tasks dropdown data
  const fetchTaskDropDownData = async (projectId: number) => {
    try {
      // Filter tasks by current user if not admin
      const params: any = { projectId };
      if (currentUser?.genericRole?.toLowerCase() !== "admin") {
        params.taskOwnerId = currentUser?.id?.toString();
      }

      const apiResponse = await tasksApis.fetchTaskDropDown(params);
      const { data, status } = apiResponse;
      if (status) {
        let options = data?.map((item: any) => {
          const overallTaskCompletionPercentage = Number(
            item.overallTaskCompletionPercentage ?? 0
          );
          return {
            label: item.taskName,
            value: item.taskId,
            type: "task",
            taskOwnerName: item.taskOwnerName, // Add task owner name
            taskOwnerId: item.taskOwnerId, // Add task owner ID
            activityId: item.activityId, // Add activity ID for resource fetching
            overallTaskCompletionPercentage,
            disabled: overallTaskCompletionPercentage >= 100,
          };
        });
        setTaskDropdownData(options);
      } else {
        setTaskDropdownData([]);
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setTaskDropdownData([]);
    }
  };

  const fetchIssueDropDownData = async (projectId: number) => {
    try {
      // Filter issues by current user if not admin
      const params: any = {};
      if (currentUser?.genericRole?.toLowerCase() !== "admin") {
        params.assignedTo = currentUser?.id?.toString();
      }

      const apiResponse = await issueApis.fetchIssueDropDown(projectId, params);
      const { data, status } = apiResponse;
      if (status) {
        let options = data?.map((item: any) => {
          return {
            label: item.issueName,
            value: item.issueId,
            type: "issue",
            issueAssignedToName: item.issueAssignedToName, // Add assigned user name
            issueAssignedToId: item.issueAssignedToId, // Add assigned user ID
          };
        });

        setIssueDropdownData(options);
      } else {
        setIssueDropdownData([]);
      }
    } catch (error) {
      console.error("Failed to fetch issues:", error);
      setIssueDropdownData([]);
    }
  };

  // Add this useEffect to update the form when linkedTask or linkedIssue changes
  useEffect(() => {
    if (modalOpen) {
      methods.reset({
        ...methods.getValues(),
        linkedTask: linkedTask || null,
        linkedIssue: linkedIssue || null,
        linkedEntity: linkedTask || linkedIssue || null,
      });
    }
  }, [modalOpen, linkedTask, linkedIssue, methods]);

  useEffect(() => {
    if (modalOpen && currentProjectId) {
      fetchTaskDropDownData(currentProjectId);
      fetchIssueDropDownData(currentProjectId);
      fetchShiftDropdownData(currentProjectId);
    } else {
      setTaskDropdownData([]);
      setIssueDropdownData([]);
      setShiftOptions([]);
    }
  }, [modalOpen, currentProjectId]);

  // Combine task and issue options for the dropdown
  useEffect(() => {
    const combinedOptions = [
      ...taskDropdownData.map((task) => ({
        ...task,
        label: `[Task] ${task.label}`,
      })),
      ...issueDropdownData.map((issue) => ({
        ...issue,
        label: `[Issue] ${issue.label}`,
      })),
    ];
    setCombinedOptions(combinedOptions as ExtendedSelectOption[]); // Cast to ExtendedSelectOption[]
  }, [taskDropdownData, issueDropdownData]);

  return (
    <Drawer
      maskClosable={false}
      title={
        <div className="d-flex justify-content-between align-items-center">
          <strong>New Time Log </strong>
          <>
            <div style={{ textAlign: "right" }}>
              {!canAddTimeLog() ? (
                <span
                  style={{
                    color: "#e74c3c",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Access Denied - Only Owner or Admin can add time logs for this
                  item
                </span>
              ) : disableSubmit ? (
                <Button color="primary" type="button">
                  <Spinner size="sm" color="light" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit(submitTimeLog)}
                  color="primary"
                  className="m-3 mt-0 mb-0 ps-4 pe-4"
                  size="sm"
                >
                  <span>Save</span>
                </Button>
              )}
            </div>
          </>
        </div>
      }
      placement="right"
      width={900}
      onClose={() => {
        reset();
        setNotes("");
        setModalOpen(false);
        setActivityResourcesList([]);
        setSelectedResourceKeys([]);
        setEditingResourceKeys([]);
        resourceForm.resetFields();
        setSelectedActivityId(null);
        // Reset tools and machinery state
        setActivityToolsList([]);
        setSelectedToolKeys([]);
        setEditingToolKeys([]);
        toolForm.resetFields();
        setMinCompletionPercentage(0);
      }}
      open={modalOpen}
      styles={{ body: { paddingBottom: 80 } }}
    >
      <Alert
        message="Time logging is not allowed for future dates and times"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitTimeLog)}>
          <br />
          <h6>Time Log Information</h6>
          <Row className="g-3">
            {/* Link Task Field (NEW) */}
            <div className="row g-3">
              <Col md="6">
                <FormSelect
                  name="linkedEntity"
                  label="Link Task/Issue"
                  required={true}
                  placeholder="Select Task/Issue"
                  options={combinedOptions}
                  defaultValue={linkedTask || linkedIssue || null} // Set default value
                  disabled={disableLinkField}
                  onChange={(value, option: any) => {
                    // Update linked task/issue fields based on selection
                    if (option?.type === "task") {
                      methods.setValue("linkedTask", Number(value));
                      methods.setValue("linkedIssue", null);
                      setSelectedEntityOwnerName(option?.taskOwnerName || null);
                      setSelectedEntityOwnerId(option?.taskOwnerId || null);
                    } else if (option?.type === "issue") {
                      methods.setValue("linkedIssue", Number(value));
                      methods.setValue("linkedTask", null);
                      setSelectedEntityOwnerName(
                        option?.issueAssignedToName || null
                      );
                      setSelectedEntityOwnerId(
                        option?.issueAssignedToId || null
                      );
                    }
                  }}
                />
              </Col>

              <Col md="6">
                <label className="form-label">User</label>
                <Input
                  value={
                    // For admin users, show selected entity owner name or task owner name
                    currentUser?.genericRole?.toLowerCase() === "admin" &&
                    selectedEntityOwnerName
                      ? selectedEntityOwnerName
                      : currentUser?.genericRole?.toLowerCase() === "admin" &&
                        taskOwnerName &&
                        disableLinkField
                      ? taskOwnerName
                      : currentUser?.firstName
                      ? `${currentUser.firstName} ${currentUser.lastName || ""}`
                      : "N/A"
                  }
                  disabled
                />
              </Col>
            </div>

            <Col md="6">
              <FormDatePicker
                name="date"
                label="Date"
                required={true}
                placeholder="Select date"
                maxDate={new Date()}
              />
            </Col>

            <Col md="6">
              <FormSelect
                name="shiftId"
                label="Shift"
                placeholder="Select Shift"
                options={shiftOptions}
              />
            </Col>
          </Row>

          <Row className="g-3 mt-2">
            <Col md="12">
              <label className="form-label">Daily Log *</label>

              <Row className="align-items-end">
                <Col md="4">
                  <FormSelect
                    name="startHour"
                    label="Start Hour"
                    placeholder="HH"
                    required={true}
                    options={Array.from({ length: 12 }, (_, i) => ({
                      label: `${i + 1}`.padStart(2, "0"),
                      value: `${i + 1}`.padStart(2, "0"),
                    }))}
                  />
                </Col>
                <Col md="4">
                  <FormSelect
                    name="startMinute"
                    label="Start Minute"
                    placeholder="MM"
                    required={true}
                    options={["00", "15", "30", "45"].map((m) => ({
                      label: m,
                      value: m,
                    }))}
                  />
                </Col>
                <Col md="4">
                  <FormSelect
                    name="startMeridiem"
                    label="AM/PM"
                    placeholder="AM/PM"
                    required={true}
                    options={[
                      { label: "AM", value: "am" },
                      { label: "PM", value: "pm" },
                    ]}
                  />
                </Col>
              </Row>

              <Row className="align-items-end mt-1">
                <Col md="4">
                  <FormSelect
                    name="endHour"
                    label="End Hour"
                    placeholder="HH"
                    required={true}
                    options={Array.from({ length: 12 }, (_, i) => ({
                      label: `${i + 1}`.padStart(2, "0"),
                      value: `${i + 1}`.padStart(2, "0"),
                    }))}
                  />
                </Col>
                <Col md="4">
                  <FormSelect
                    name="endMinute"
                    label="End Minute"
                    placeholder="MM"
                    required={true}
                    options={["00", "15", "30", "45"].map((m) => ({
                      label: m,
                      value: m,
                    }))}
                  />
                </Col>
                <Col md="4">
                  <FormSelect
                    name="endMeridiem"
                    label="AM/PM"
                    placeholder="AM/PM"
                    required={true}
                    options={[
                      { label: "AM", value: "am" },
                      { label: "PM", value: "pm" },
                    ]}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md="6">
              <FormSelect
                name="billingType"
                label="Billing Type"
                placeholder="Select"
                options={[
                  { value: "Billable", label: "Billable" },
                  { value: "NonBillable", label: "NonBillable" },
                ]}
              />
            </Col>
          </Row>

          {/* Completion Percentage - Only show for tasks */}
          {selectedEntity &&
            combinedOptions.find((opt) => opt.value === selectedEntity)
              ?.type === "task" && (
              <Row className="g-3 mt-1">
                <Col md="12">
                  <Controller
                    name="taskCompletionPercentage"
                    control={methods.control}
                    render={({ field, fieldState }) => {
                      const rawValue =
                        typeof field.value === "number"
                          ? field.value
                          : field.value !== null && field.value !== undefined
                          ? Number(field.value)
                          : minCompletionPercentage;
                      const numValue = Math.max(
                        minCompletionPercentage,
                        rawValue
                      );
                      const sliderMarks: Record<number, string> = {
                        0: "0%",
                        25: "25%",
                        50: "50%",
                        75: "75%",
                        100: "100%",
                      };
                      if (
                        minCompletionPercentage > 0 &&
                        !sliderMarks[minCompletionPercentage]
                      ) {
                        sliderMarks[
                          minCompletionPercentage
                        ] = `${minCompletionPercentage}%`;
                      }
                      const addedPercent = numValue - minCompletionPercentage;
                      return (
                        <div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: 4,
                            }}
                          >
                            <label
                              className="form-label mb-0"
                              style={{ fontWeight: 500 }}
                            >
                              Completion Percentage{" "}
                              <span className="text-danger">*</span>
                              {minCompletionPercentage > 0 && (
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 400,
                                    color: "#666",
                                    marginLeft: 8,
                                  }}
                                >
                                  (Current: {minCompletionPercentage}%
                                  {addedPercent > 0
                                    ? ` · Adding: ${addedPercent}%`
                                    : ""}
                                  )
                                </span>
                              )}
                            </label>
                            <span
                              style={{
                                fontWeight: 600,
                                color: "#1677ff",
                                fontSize: 14,
                              }}
                            >
                              {numValue}%
                            </span>
                          </div>
                          <Slider
                            min={0}
                            max={100}
                            step={1}
                            value={numValue}
                            onChange={(v) => {
                              const next = Math.max(
                                minCompletionPercentage,
                                v as number
                              );
                              methods.setValue(
                                "taskCompletionPercentage",
                                next as any,
                                {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                  shouldTouch: true,
                                }
                              );
                              methods.clearErrors("taskCompletionPercentage");
                            }}
                            marks={sliderMarks}
                            tooltip={{ formatter: (v) => `${v}%` }}
                          />
                          {fieldState.error && (
                            <div
                              className="text-danger"
                              style={{ fontSize: 12, marginTop: 4 }}
                            >
                              {fieldState.error.message}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                </Col>
              </Row>
            )}

          {/* Resource Allocation Table - Only for tasks */}
          {watchedLinkedTask && (
            <div className="row g-3 mt-3">
              <div className="col-12">
                <h6 className="title">Allocate Resources</h6>
                <Form
                  form={resourceForm}
                  component={false}
                  onValuesChange={() => forceRecalc()}
                >
                  <Table
                    rowKey={(record) => `${record.activityResourceId}`}
                    columns={[
                      {
                        title: "Labour Type",
                        dataIndex: "laborTypeName",
                        key: "laborTypeName",
                        width: 180,
                        align: "center",
                      },
                      {
                        title: "Labour Category",
                        dataIndex: "laborCategory",
                        key: "laborCategory",
                        width: 180,
                        align: "center",
                      },
                      {
                        title: "Resource Count",
                        dataIndex: "resourceCount",
                        key: "resourceCount",
                        width: 130,
                        align: "center",
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Planned Qty",
                        dataIndex: "plannedQuantity",
                        key: "plannedQuantity",
                        width: 120,
                        align: "center",
                        render: (text: any, record: any) => {
                          if (text === undefined || text === null || text === "")
                            return "-";
                          const uom = resolveUomName(record);
                          return `${text}${uom ? ` ${uom}` : ""}`;
                        },
                      },
                      {
                        title: "Planned Rate",
                        dataIndex: "plannedRate",
                        key: "plannedRate",
                        width: 120,
                        align: "center",
                        render: (text: any) =>
                          text !== undefined && text !== null && text !== ""
                            ? text
                            : "-",
                      },
                      {
                        title: "Resource at Site",
                        dataIndex: "resourceAtSite",
                        key: "resourceAtSite",
                        width: 140,
                        align: "center",
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "resourceAtSite",
                          title: "Resource at Site",
                          form: resourceForm,
                          editing: isResourceEditing(record),
                        }),
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Actual Quantity",
                        dataIndex: "actualQuantity",
                        key: "actualQuantity",
                        width: 140,
                        align: "center",
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "actualQuantity",
                          title: "Actual Quantity",
                          form: resourceForm,
                          editing: isResourceEditing(record),
                        }),
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Actual Rate",
                        dataIndex: "actualRate",
                        key: "actualRate",
                        width: 140,
                        align: "center",
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "actualRate",
                          title: "Actual Rate",
                          form: resourceForm,
                          editing: isResourceEditing(record),
                        }),
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Total Amount",
                        key: "resourceTotalAmount",
                        width: 150,
                        align: "center",
                        render: (_: any, record: any) => {
                          const key = `${record.activityResourceId}`;
                          if (!selectedResourceKeys.map(String).includes(key))
                            return "";
                          const total = resourceLineTotal(
                            resourceForm.getFieldValue(`resourceAtSite_${key}`),
                            resourceForm.getFieldValue(`actualQuantity_${key}`),
                            resourceForm.getFieldValue(`actualRate_${key}`)
                          );
                          return total ? formatCurrency(total) : "";
                        },
                      },
                    ]}
                    rowSelection={{
                      selectedRowKeys: selectedResourceKeys,
                      onChange: onResourceSelectChange,
                    }}
                    components={{
                      body: {
                        cell: ResourceEditableCell,
                      },
                    }}
                    dataSource={activityResourcesList}
                    pagination={false}
                    bordered
                    size="middle"
                    scroll={{ x: 1200 }}
                  />
                </Form>
                {activityResourcesList.length > 0 && (
                  <div
                    className="d-flex justify-content-end align-items-center mt-2"
                    style={{ gap: 8, fontWeight: 600, color: "#004293" }}
                  >
                    <span>Resources Total:</span>
                    <span style={{ fontSize: 15 }}>
                      {formatCurrency(computeResourceSectionTotal())}
                    </span>
                  </div>
                )}
                {activityResourcesList.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                    }}
                  >
                    No resources available for this task. Please check if the
                    task has an associated activity with resources.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tools & Machinery Table - Only for tasks */}
          {watchedLinkedTask && (
            <div className="row g-3 mt-3">
              <div className="col-12">
                <h6 className="title">Tools & Machinery</h6>
                <Form
                  form={toolForm}
                  component={false}
                  onValuesChange={() => forceRecalc()}
                >
                  <Table
                    rowKey={(record) => `${record.taskToolId}`}
                    columns={[
                      {
                        title: "Tool Type",
                        dataIndex: "toolTypeName",
                        key: "toolTypeName",
                        width: 160,
                        align: "center",
                      },
                      {
                        title: "Tool/Machinery",
                        dataIndex: "toolName",
                        key: "toolName",
                        width: 160,
                        align: "center",
                      },
                      {
                        title: "Serial Number",
                        dataIndex: "serialNumber",
                        key: "serialNumber",
                        width: 140,
                        align: "center",
                        render: (text: any) =>
                          text !== undefined && text !== null && text !== ""
                            ? text
                            : "-",
                      },
                      // Count column hidden for tools in timelogs
                      // {
                      //   title: "Count",
                      //   dataIndex: "count",
                      //   key: "count",
                      //   width: 110,
                      //   align: "center",
                      //   render: (text: any) => text || "",
                      // },
                      {
                        title: "Planned Qty",
                        dataIndex: "plannedQuantity",
                        key: "plannedQuantity",
                        width: 120,
                        align: "center",
                        render: (text: any, record: any) => {
                          if (text === undefined || text === null || text === "")
                            return "-";
                          const uom = resolveUomName(record);
                          return `${text}${uom ? ` ${uom}` : ""}`;
                        },
                      },
                      {
                        title: "Planned Rate",
                        dataIndex: "plannedRate",
                        key: "plannedRate",
                        width: 120,
                        align: "center",
                        render: (text: any) =>
                          text !== undefined && text !== null && text !== ""
                            ? text
                            : "-",
                      },
                      {
                        title: "Actual Quantity",
                        dataIndex: "actualQuantity",
                        key: "actualQuantity",
                        width: 140,
                        align: "center",
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "actualQuantity",
                          title: "Actual Quantity",
                          form: toolForm,
                          editing: isToolEditing(record),
                        }),
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Actual Rate",
                        dataIndex: "actualRate",
                        key: "actualRate",
                        width: 140,
                        align: "center",
                        onCell: (record: any) => ({
                          record,
                          dataIndex: "actualRate",
                          title: "Actual Rate",
                          form: toolForm,
                          editing: isToolEditing(record),
                        }),
                        render: (text: any) => text || "",
                      },
                      {
                        title: "Total Amount",
                        key: "toolTotalAmount",
                        width: 150,
                        align: "center",
                        render: (_: any, record: any) => {
                          const key = `${record.taskToolId}`;
                          if (!selectedToolKeys.map(String).includes(key))
                            return "";
                          const total = toolLineTotal(
                            toolForm.getFieldValue(`actualQuantity_${key}`),
                            toolForm.getFieldValue(`actualRate_${key}`)
                          );
                          return total ? formatCurrency(total) : "";
                        },
                      },
                      {
                        title: "Materials",
                        key: "toolMaterials",
                        width: 150,
                        align: "center",
                        render: (_: any, record: any) => {
                          const id = record.taskToolId;
                          const selected = selectedToolKeys
                            .map(String)
                            .includes(`${id}`);
                          const planned = (record.toolMaterials || []).length;
                          const filled = (materialRowsByTool[id] || []).filter(
                            (r) =>
                              r.actualQuantity != null &&
                              `${r.actualQuantity}` !== "",
                          ).length;
                          return (
                            <Button
                              type="button"
                              color="primary"
                              size="sm"
                              outline={!filled}
                              disabled={!selected || !planned}
                              title={
                                !planned
                                  ? "No planned materials on this tool"
                                  : selected
                                  ? "Log actual material usage"
                                  : "Select this tool first"
                              }
                              onClick={() => setMaterialsDrawerToolId(id)}
                            >
                              <em className="icon ni ni-list" />{" "}
                              {planned
                                ? `Materials (${filled}/${planned})`
                                : "No Materials"}
                            </Button>
                          );
                        },
                      },
                    ]}
                    rowSelection={{
                      selectedRowKeys: selectedToolKeys,
                      onChange: onToolSelectChange,
                    }}
                    components={{
                      body: {
                        cell: ToolEditableCell,
                      },
                    }}
                    dataSource={activityToolsList}
                    pagination={false}
                    bordered
                    size="middle"
                    scroll={{ x: 1200 }}
                  />
                </Form>

                {/* Per-tool consumed-materials drawer */}
                <Drawer
                  title={
                    materialsDrawerToolId != null
                      ? `Consumed Materials — ${
                          activityToolsList.find(
                            (t) => t.taskToolId === materialsDrawerToolId,
                          )?.toolName || "Tool"
                        }`
                      : "Consumed Materials"
                  }
                  placement="right"
                  width={Math.min(
                    980,
                    typeof window !== "undefined" ? window.innerWidth - 80 : 980,
                  )}
                  open={materialsDrawerToolId != null}
                  onClose={() => setMaterialsDrawerToolId(null)}
                  destroyOnClose
                  footer={
                    <div style={{ textAlign: "right" }}>
                      <Button
                        type="button"
                        color="primary"
                        onClick={() => {
                          setMaterialsDrawerToolId(null);
                          message.success("Materials saved");
                        }}
                      >
                        <em className="icon ni ni-check-circle" /> Save Materials
                      </Button>
                    </div>
                  }
                >
                  {materialsDrawerToolId != null && (
                    <TimeLogToolMaterialsEditor
                      rows={getToolMaterialRows(materialsDrawerToolId)}
                      materialNameMap={materialNameMap}
                      onChange={(rows) =>
                        setMaterialRowsByTool((prev) => ({
                          ...prev,
                          [materialsDrawerToolId as number]: rows,
                        }))
                      }
                    />
                  )}
                </Drawer>
                {activityToolsList.length > 0 && (
                  <div
                    className="d-flex justify-content-end align-items-center mt-2"
                    style={{ gap: 8, fontWeight: 600, color: "#004293" }}
                  >
                    <span>Tools Total:</span>
                    <span style={{ fontSize: 15 }}>
                      {formatCurrency(computeToolSectionTotal())}
                    </span>
                  </div>
                )}
                {(activityResourcesList.length > 0 ||
                  activityToolsList.length > 0) && (
                  <div
                    className="d-flex justify-content-end align-items-center mt-2 pt-2"
                    style={{
                      gap: 8,
                      fontWeight: 700,
                      color: "#004293",
                      borderTop: "1px solid #e8e8e8",
                    }}
                  >
                    <span>Grand Total:</span>
                    <span style={{ fontSize: 16 }}>
                      {formatCurrency(
                        computeResourceSectionTotal() +
                          computeToolSectionTotal()
                      )}
                    </span>
                  </div>
                )}
                {activityToolsList.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                    }}
                  >
                    No tools available for this task. Please check if the task
                    has associated tools.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* File Upload Section */}
          <div className="row g-3 mt-3">
            <div className="col-12">
              <div
                style={{
                  border: "1px dashed #d9d9d9",
                  borderRadius: "4px",
                  padding: "8px 12px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 500 }}>
                    Drop files or add Attachments Files
                  </div>
                </div>
                <Upload.Dragger
                  multiple
                  showUploadList={false}
                  customRequest={({ file, onSuccess }) => {
                    setTimeout(() => {
                      onSuccess?.("ok");
                      handleFileUpload({
                        file: { originFileObj: file, status: "done" },
                      });
                    }, 300);
                  }}
                  onRemove={handleRemoveFile}
                  style={{ background: "transparent", padding: "4px 0" }}
                >
                  <div
                    style={{ textAlign: "left", fontSize: 12, color: "#555" }}
                  >
                    <UploadOutlined style={{ marginRight: 8 }} />
                    Drop files or click to upload
                  </div>
                </Upload.Dragger>
                {attachedFiles.length > 0 && (
                  <div style={{ marginTop: "10px" }}>
                    {attachedFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          fontSize: 12,
                          padding: "4px 0",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>📎 {file.name}</span>
                          <Button
                            className="btn-icon"
                            size="sm"
                            onClick={() => handleRemoveFile(file)}
                            style={{
                              background: "transparent",
                              border: "none",
                              boxShadow: "none",
                              padding: 0,
                              fontSize: 12,
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                        <Input
                          placeholder="Enter remarks for this file"
                          value={fileRemarks[index] || ""}
                          onChange={(e) => {
                            setFileRemarks((prev) => ({
                              ...prev,
                              [index]: e.target.value,
                            }));
                            setNewFileData((prev) =>
                              prev.map((item, i) =>
                                i === index
                                  ? { ...item, remarks: e.target.value }
                                  : item
                              )
                            );
                          }}
                          style={{
                            marginTop: "4px",
                            fontSize: "12px",
                            height: "24px",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="row g-3 mt-1">
            <div className="col-12">
              <label className="form-label">Notes</label>
              <Input.TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter notes"
                rows={4}
                style={{
                  resize: "vertical",
                }}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </Drawer>
  );
};

export default TimeSheetListCreation;
